import { useMemo, useState } from "react";
import { EventCard, LiveEventCard } from "../components/EventCard";
import { PlayersDashboard } from "../components/PlayersDashboard";
import { Spinner } from "../components/bits";
import type { WindowInfo } from "../lib/api";
import { baseEventId, eventTier, regionalState, type GroupedEvent } from "../lib/events";
import { useApp } from "../lib/store";

export function Home() {
  const { grouped, status, storedByEvent, eventsError, offline, region } = useApp();

  const trackedBases = useMemo(
    () => new Set((status?.tracked ?? []).map(t => t.event_base)),
    [status],
  );
  const watchedByBase = useMemo(() => {
    const m = new Map<string, WindowInfo[]>();
    for (const w of status?.windows ?? []) {
      const base = baseEventId(w.event_id);
      m.set(base, [...(m.get(base) ?? []), w]);
    }
    return m;
  }, [status]);

  // Live / Upcoming / Recent for the featured events; everything the tier
  // classifier demotes (dev/test cups, etc.) collects in one "Other events"
  // section so the front page stays about the tournaments that matter —
  // including demoted events that are live right now. A live test cup still
  // reads as live (EventCard keeps its live badge), it just sits in "Other
  // events" rather than crowding the main Live section. The dashboard above is
  // the personal view; there's no watchlist row.
  const buckets = useMemo(() => {
    const now = Date.now();
    const out = {
      live: [] as GroupedEvent[],
      upcoming: [] as GroupedEvent[],
      recent: [] as GroupedEvent[],
      otherLive: [] as GroupedEvent[],
      otherUpcoming: [] as GroupedEvent[],
      otherRecent: [] as GroupedEvent[],
    };
    for (const g of grouped ?? []) {
      const st = regionalState(g, region, now);
      const demoted = eventTier(g) === "other";
      if (st === "live") (demoted ? out.otherLive : out.live).push(g);
      else if (st === "upcoming" || st === "later") {
        (demoted ? out.otherUpcoming : out.upcoming).push(g);
      } else if (st === "recent") {
        (demoted ? out.otherRecent : out.recent).push(g);
      }
    }
    out.live.sort((a, b) => a.pivot_time - b.pivot_time);
    out.upcoming.sort((a, b) => a.pivot_time - b.pivot_time);
    out.recent.sort((a, b) => b.pivot_time - a.pivot_time);
    out.otherLive.sort((a, b) => a.pivot_time - b.pivot_time);
    out.otherUpcoming.sort((a, b) => a.pivot_time - b.pivot_time);
    out.otherRecent.sort((a, b) => b.pivot_time - a.pivot_time);
    return out;
  }, [grouped, region]);

  // Live-first, then upcoming, then recent — same ordering logic the featured
  // sections use.
  const other = useMemo(
    () => [...buckets.otherLive, ...buckets.otherUpcoming, ...buckets.otherRecent],
    [buckets],
  );

  const stored = (g: GroupedEvent) => storedByEvent?.get(g.base_id)?.length ?? 0;

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      <PlayersDashboard />

      {offline && (
        <Notice tone="err">Server unreachable — showing whatever was last loaded.</Notice>
      )}
      {eventsError && !offline && <Notice tone="warn">{eventsError}</Notice>}
      {!grouped && !eventsError && <Spinner label="loading the event catalog" />}

      {buckets.live.length > 0 && (
        <Section title="Live events">
          <div className="space-y-4">
            {buckets.live.map(g => (
              <LiveEventCard
                key={g.base_id}
                group={g}
                region={region}
                isTracked={trackedBases.has(g.base_id)}
                watchedWindows={watchedByBase.get(g.base_id) ?? []}
              />
            ))}
          </div>
        </Section>
      )}

      {buckets.upcoming.length > 0 && (
        <Section title="Upcoming">
          <CardGrid>
            {buckets.upcoming.map((g, i) => (
              <EventCard
                key={g.base_id}
                group={g}
                region={region}
                isTracked={trackedBases.has(g.base_id)}
                storedCount={stored(g)}
                index={i}
              />
            ))}
          </CardGrid>
        </Section>
      )}

      {buckets.recent.length > 0 && (
        <Section title="Recently played">
          <CardGrid>
            {buckets.recent.map((g, i) => (
              <EventCard
                key={g.base_id}
                group={g}
                region={region}
                isTracked={trackedBases.has(g.base_id)}
                storedCount={stored(g)}
                index={i}
              />
            ))}
          </CardGrid>
        </Section>
      )}

      {other.length > 0 && (
        <OtherSection count={other.length}>
          <CardGrid>
            {other.map((g, i) => (
              <EventCard
                key={g.base_id}
                group={g}
                region={region}
                isTracked={trackedBases.has(g.base_id)}
                storedCount={stored(g)}
                index={i}
              />
            ))}
          </CardGrid>
        </OtherSection>
      )}
    </main>
  );
}

/** Collapsed-by-default home for demoted events (minor / test cups). */
function OtherSection({ count, children }: { count: number; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mb-10">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="press group mb-3 flex w-full items-center gap-2 text-[17px] text-dim transition hover:text-fg"
      >
        <span className="display flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-faint group-hover:bg-dim" />
          Other events
        </span>
        <span className="font-mono text-[12px] text-faint">{count}</span>
        <svg
          viewBox="0 0 12 12"
          className={`ml-0.5 h-3 w-3 text-faint transition-transform ${open ? "rotate-90" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M4.5 2.5 8 6l-3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && children}
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="display mb-3 flex items-center gap-2 text-[17px] text-fg">
        <span className="h-1.5 w-1.5 rounded-full bg-accent" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function CardGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>;
}

function Notice({ tone, children }: { tone: "err" | "warn"; children: React.ReactNode }) {
  const cls =
    tone === "err"
      ? "border-err/30 bg-err/[0.07] text-err"
      : "border-warn/30 bg-warn/[0.07] text-warn";
  return <p className={`mb-6 rounded-lg border px-4 py-3 text-xs ${cls}`}>{children}</p>;
}
